/**
 * DISPLAYING A LINE'S PRODUCT AND SIZE WITHOUT SAYING THE SIZE TWICE.
 *
 * Our product names already END with the size - "Membrane Wrap (Q4205) 4x8", and for the awkward
 * ones "Microlyte SAM (A2005) 16mm Disc". Verified against the catalog: all 18 real products follow
 * it. So a display that appends line.size unconditionally renders "Membrane Wrap (Q4205) 4x8 (4x8)".
 *
 * The size is NOT redundant data and must never be dropped from the record - billed units are
 * quantity x units-per-size (SIZE_UNITS on the backend), and a line with no size sends the invoice
 * to needs_attention rather than guessing an amount. This is a rendering rule only; nothing here
 * touches what is stored or sent.
 *
 * APPEND ONLY WHEN THE NAME DOES NOT ALREADY CARRY IT. That is deliberately conditional rather
 * than "never append": a product named without its size still needs it shown, and this way the
 * behaviour does not depend on every future catalog entry following the convention.
 *
 * Byte-for-byte the same rule as describeLine() in the backend's invoices.service.ts, which puts
 * the description on the actual QuickBooks invoice. The two must agree - an admin comparing the
 * app against QuickBooks is comparing these two functions - so keep them in sync. That backend fix
 * has regressed once already (a stale working copy restored the old line), which is why the rule
 * lives in one exported function here instead of being inlined at each call site.
 */

/** True when appending `size` to `name` would state the size twice (or add nothing at all). */
function alreadyStated(name, size) {
  const base = String(name ?? '').trim().toLowerCase();
  const sz = String(size ?? '').trim().toLowerCase();
  return !sz || base.endsWith(sz);
}

/**
 * Space-joined - the invoice breakdown form, matching the backend's describeLine so the app and
 * the QuickBooks line read identically: "Membrane Wrap (Q4205) 4x8".
 */
export function describeLine(name, size) {
  const base = String(name ?? '').trim();
  return alreadyStated(name, size) ? base : `${base} ${String(size).trim()}`;
}

/**
 * Parenthesised - the Order Details line form: "Membrane Wrap (Q4205) 4x8", or
 * "Collagen Dressing (DME 1gram)" when the name does not carry it. Falls back to 'N/A' for a line
 * whose product failed to populate, exactly as the old inline expression did.
 */
export function productLabel(name, size) {
  const base = String(name ?? '').trim() || 'N/A';
  return alreadyStated(name, size) ? base : `${base} (${String(size).trim()})`;
}
